import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Box,
    TextField,
    Grid,
    Skeleton,
    Menu,
    MenuItem,
    InputAdornment,
    IconButton,
    Tooltip,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { performanceApi, commonApi, clientMasterApi } from '@/services/api';
import { useHoldingsDateRestriction } from '@/hooks/useHoldingsDateRestriction';
import { formatCurrency, formatDate, formatQuantity } from '@/utils/formatters';
import ErrorDisplay from '../utils/ErrorDisplay';
import { ColumnDef } from '../utils/DataTable';
import {
    SectionCard,
    StaticTable,
    SummaryGrid,
    SummaryItem,
    ReturnCell,
    BenchmarkCell,
    ShareCell,
    ReportHeading,
    AllocationBreakdown,
    maxShare,
    LONG_LIST_MAX_HEIGHT,
} from './ReportSections';
import { PageFrame, PageHeader, Surface, Button, Note, Placeholder, FilterAsyncSelect, Pill, Tag, SignedValue, Icon } from '@/components/ui';
import IFSCPerformanceReport, { IFSCReportData } from './IFSCPerformanceReport';
import {
  Client,
  SelectOption,
  IFSC_AIF_FIRM_TYPE,
  IFSC_REPORT_TYPE,
  IBKR_REPORT_TYPE,
  SP500_BENCHMARK_LABEL,
  BSE500_BENCHMARK_LABEL,
} from '@/types/client';
import styles from '../utils/shared.module.css';
import reportStyles from './ClientPerformance.module.css';

interface AssetAllocation {
    asset_class: string;
    market_value: string;
    percentage: string;
}

interface SectorAllocation {
    sector_name: string;
    market_value: string;
    percentage: string;
}

interface Holding {
    symbol_code: string;
    sector_name: string;
    asset_class: string;
    quantity: number;
    market_value: string;
    percentage: string;
}

interface PerformanceMetrics {
    [key: string]: number | null;
}

// AIF nav-unit reconciliation warning emitted by the backend (empty for PMS clients).
interface PerformanceWarning {
    type: string;
    from_date: string;
    message: string;
    expected_nav_units?: number;
    actual_nav_units?: number;
    unit_difference?: number;
}

interface PerformanceData {
    access_restricted?: boolean;
    holding_date: string;
    // 'IFSC_AIF' switches the results area to the series-accounted IFSC layout
    // (payload shape is IFSCReportData; other fields here are absent). 'IBKR'
    // reports render through this same standard layout — see `currency` below.
    report_type?: string;
    // True when an AIF/IFSC/IBKR mid-period date was snapped to the last reliable
    // valuation date (AIF/IFSC: last month-end revaluation; IBKR: last date the
    // account value wasn't an unreconciled zero from an incomplete data pull).
    nav_date_snapped?: boolean;
    inception_date: string;
    // AIF clients only: nav-unit reconciliation alerts; absent/empty otherwise.
    warnings?: PerformanceWarning[];
    net_capital_in_out: string;
    total_contribution: string;
    total_withdrawal: string;
    unrealized_gain: string;
    total_portfolio_value: string;
    total_fees_expenses: string;
    total_accrued_income: string;
    // Absent for an IBKR client with no Holdings snapshot for the requested date.
    asset_allocation?: AssetAllocation[];
    // Omitted for AIF clients (no instrument-level breakdown).
    sector_allocation?: SectorAllocation[];
    holdings?: Holding[];
    twrr: PerformanceMetrics;
    xirr: PerformanceMetrics;
    benchmark?: PerformanceMetrics;
    benchmark_irr?: PerformanceMetrics;
    // Present (e.g. "USD") only for IBKR reports; absent -> formatCurrency defaults to INR.
    currency?: string;
}

interface PeriodRow { period: string; value: number | null; benchmark: number | null; }

// Both payload shapes arrive from the same endpoint; report_type discriminates them
// so no casting is needed at the render site. An IBKR report ('IBKR') is NOT IFSC, so
// it naturally falls through to the standard PerformanceData layout below.
const isIfscReportData = (data: PerformanceData | IFSCReportData): data is IFSCReportData =>
    data.report_type === IFSC_REPORT_TYPE;

const prettifyPeriod = (period: string) =>
    period.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

// Column definitions for each section's embedded DataTable. Builder functions
// (rather than static arrays) so $ formatting can use the report's own currency
// (USD for IBKR, INR default for Nuvama PMS/AIF), and share bars can scale
// against the largest row of the table they sit in.
// Benchmark differs by report type: BSE500 for Nuvama PMS/AIF, S&P 500 for IBKR
// (USD-denominated accounts vs. an INR index would not be a meaningful comparison).
const buildTwrrColumns = (benchmarkLabel: string): ColumnDef<PeriodRow>[] => [
    { key: 'period', header: 'Period', render: row => prettifyPeriod(row.period) },
    { key: 'value', header: 'TWRR', align: 'right', render: row => <ReturnCell value={row.value} /> },
    { key: 'benchmark', header: benchmarkLabel, align: 'right', render: row => <BenchmarkCell value={row.benchmark} /> },
];

const buildXirrColumns = (benchmarkLabel: string): ColumnDef<PeriodRow>[] => [
    { key: 'period', header: 'Period', render: row => prettifyPeriod(row.period) },
    { key: 'value', header: 'XIRR', align: 'right', render: row => <ReturnCell value={row.value} /> },
    { key: 'benchmark', header: benchmarkLabel, align: 'right', render: row => <BenchmarkCell value={row.benchmark} /> },
];

const buildSectorColumns = (currency?: string, max = 100): ColumnDef<SectorAllocation>[] => [
    { key: 'sector_name', header: 'Sector', render: row => row.sector_name },
    { key: 'market_value', header: 'Market Value', align: 'right', render: row => formatCurrency(row.market_value, currency) },
    { key: 'percentage', header: '%', align: 'right', render: row => <ShareCell value={row.percentage} max={max} /> },
];

const buildHoldingsColumns = (currency?: string, max = 100): ColumnDef<Holding>[] => [
    { key: 'symbol_code', header: 'Symbol', render: row => <span className={reportStyles.symbol}>{row.symbol_code}</span> },
    { key: 'sector_name', header: 'Sector', render: row => row.sector_name },
    { key: 'asset_class', header: 'Asset Class', render: row => <Tag>{row.asset_class}</Tag> },
    { key: 'quantity', header: 'Qty', align: 'right', render: row => formatQuantity(row.quantity) },
    { key: 'market_value', header: 'Market Value', align: 'right', render: row => formatCurrency(row.market_value, currency) },
    { key: 'percentage', header: '%', align: 'right', render: row => <ShareCell value={row.percentage} max={max} /> },
];

// AIF holdings come from the pooled parent account, so absolute market value /
// quantity are pool-level (not investor-level) and the backend omits them — show
// only the allocation percentages for AIF clients.
const buildSectorColumnsAif = (currency?: string, max = 100): ColumnDef<SectorAllocation>[] =>
    buildSectorColumns(currency, max).filter(col => col.key !== 'market_value');

const buildHoldingsColumnsAif = (currency?: string, max = 100): ColumnDef<Holding>[] =>
    buildHoldingsColumns(currency, max).filter(col => col.key !== 'market_value' && col.key !== 'quantity');

// Date presets for the holding-date "Quick select" — each resolves to a concrete date.
const toISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getDatePresets = (): Array<{ label: string; value: string }> => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0 = Jan
    const prevMonthEnd = new Date(year, month, 0);
    const prevQuarterEnd = new Date(year, Math.floor(month / 3) * 3, 0);
    // Indian financial year ends 31 Mar (month index 2). Use the most recent one.
    const prevFinYearEnd = month >= 3 ? new Date(year, 2, 31) : new Date(year - 1, 2, 31);
    const prevCalYearEnd = new Date(year - 1, 11, 31);
    return [
        { label: 'Previous Month End', value: toISO(prevMonthEnd) },
        { label: 'Previous Quarter End', value: toISO(prevQuarterEnd) },
        { label: 'Previous Financial Year End', value: toISO(prevFinYearEnd) },
        { label: 'Previous Calendar Year End', value: toISO(prevCalYearEnd) },
    ];
};

const getClientName = (client: Client | undefined) => {
    if (!client) return '';
    return client.full_name || `${client.first_name} ${client.middle_name || ''} ${client.last_name}`.trim();
};

const ClientPerformance = () => {
    const [searchParams] = useSearchParams();
    const [clients, setClients] = useState<Client[]>([]);
    const initialClientId = useMemo(() => {
        const param = searchParams.get('client_id');
        if (!param) return null;
        const id = parseInt(param, 10);
        return isNaN(id) ? null : id;
    }, [searchParams]);
    const [selectedClient, setSelectedClient] = useState<number | null>(initialClientId);
    // The client the currently-displayed report was loaded for (independent of the
    // dropdown selection, so the header stays correct if the user changes the picker).
    const [loadedClientId, setLoadedClientId] = useState<number | null>(null);
    const [holdingDate, setHoldingDate] = useState('');
    // True once the default-holding-date lookup has settled (either way).
    const [holdingDateLookupDone, setHoldingDateLookupDone] = useState(false);
    const [performanceData, setPerformanceData] = useState<PerformanceData | IFSCReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAccessRestricted, setIsAccessRestricted] = useState(false);
    const [quickAnchor, setQuickAnchor] = useState<null | HTMLElement>(null);
    const { accessCutoffDate, restrictionLoaded, isDateRestricted, clampDate } = useHoldingsDateRestriction();

    // Snap holdingDate back to accessCutoffDate if restriction loads after a too-recent date is set
    useEffect(() => {
        if (isDateRestricted(holdingDate)) {
            setHoldingDate(clampDate(holdingDate));
        }
    }, [accessCutoffDate, holdingDate, isDateRestricted, clampDate]);

    // Merges a batch of clients into state by id, so a client found by an earlier
    // search (or the deep-link fetch below) keeps its entry once a later search
    // no longer returns it.
    const mergeClients = useCallback((incoming: Client[]) => {
        setClients(prev => {
            const byId = new Map(prev.map(c => [c.id, c]));
            incoming.forEach(c => byId.set(c.id, c));
            return Array.from(byId.values());
        });
    }, []);

    // Server-searched client options for the picker below — replaces an eager
    // page_size: 1000 fetch of every client on mount. No client_type filter ->
    // backend returns PMS, AIF and IFSC AIF clients; each renders its matching
    // report layout.
    const loadClientOptions = useCallback(async (search: string): Promise<SelectOption[]> => {
        try {
            const response = await clientMasterApi.getClients({ search, page_size: 100 });
            mergeClients(response.results);
            return response.results.map(client => {
                const typeSuffix = client.client_type === 'AIF'
                    ? (client.firm_type === IFSC_AIF_FIRM_TYPE ? ' (IFSC AIF)' : ' (AIF)')
                    : '';
                return {
                    value: String(client.id),
                    label: `${client.client_code} - ${getClientName(client)}${typeSuffix}`,
                    id: client.id,
                };
            });
        } catch (err) {
            setError('Failed to load clients');
            console.error('Error loading clients:', err);
            return [];
        }
    }, [mergeClients]);

    const autoLoadedRef = useRef(false);

    // Reset the auto-load gate whenever the deep-link client changes so
    // navigating from ?client_id=1 to ?client_id=2 re-triggers the fetch.
    useEffect(() => {
        autoLoadedRef.current = false;
    }, [initialClientId]);

    useEffect(() => {
        setPerformanceData(null);
    }, []);

    // A deep link (?client_id=) may name a client the default search never
    // surfaces — fetch it directly so the picker (and the results header, before
    // the report itself has loaded) show its name instead of appearing empty.
    useEffect(() => {
        if (initialClientId == null) return;
        let cancelled = false;
        setLoadingClients(true);
        clientMasterApi.getClient(initialClientId)
            .then(client => {
                if (cancelled) return;
                mergeClients([client]);
            })
            .catch(err => console.error('Error loading deep-linked client:', err))
            .finally(() => {
                if (!cancelled) setLoadingClients(false);
            });
        return () => { cancelled = true; };
    }, [initialClientId, mergeClients]);

    // Default the holding date to the last working day (same as the Holdings pages).
    // clampDate keeps it within the access ceiling for date-restricted users.
    useEffect(() => {
        let cancelled = false;
        commonApi.getLastWorkingDay()
            .then(data => {
                if (!cancelled && data.last_working_day) {
                    setHoldingDate(clampDate(data.last_working_day));
                }
            })
            .catch(err => console.error('Error fetching last working day:', err))
            .finally(() => {
                if (!cancelled) setHoldingDateLookupDone(true);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // NOTE: empty dependency array — this callback must only close over stable
    // state setters. If you add a read of non-setter state here, add it to the
    // dependency array too, or it will capture a stale value.
    const fetchPerformanceData = useCallback(async (clientId: number, date: string) => {
        try {
            setLoading(true);
            setError(null);
            setIsAccessRestricted(false);
            setPerformanceData(null);
            // Clear the previous report's client so an error/restricted response can't
            // leave a stale client name + AIF chip in the header.
            setLoadedClientId(null);
            const response = await performanceApi.getClientPerformance({
                client_master_id: clientId.toString(),
                date,
            });
            if (response.access_restricted) {
                setIsAccessRestricted(true);
                return;
            }
            setLoadedClientId(clientId);
            setPerformanceData(response);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load performance data');
            setPerformanceData(null);
            console.error('Error loading performance data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-load when navigated with ?client_id=, once a holding date is available.
    // Apply the same date-restriction guard as the manual Load button.
    useEffect(() => {
        if (!initialClientId || !holdingDate || autoLoadedRef.current) return;
        // Wait for restriction to load so clampDate has already adjusted holdingDate
        // before we fire the fetch — avoids a race where we fetch with a too-recent
        // date and get access_restricted back from the API.
        if (!restrictionLoaded) return;
        if (isDateRestricted(holdingDate)) return;
        autoLoadedRef.current = true;
        fetchPerformanceData(initialClientId, holdingDate);
    }, [holdingDate, initialClientId, fetchPerformanceData, isDateRestricted, accessCutoffDate, restrictionLoaded]);

    // A deep-linked visit auto-loads once the holding date and restriction are known;
    // show the loader for that wait instead of the "No performance report yet" prompt.
    // Stops waiting if the date lookup yields nothing (the manual prompt applies then).
    const awaitingAutoLoad = !!initialClientId && !autoLoadedRef.current && !error
        && (holdingDate ? !restrictionLoaded || isDateRestricted(holdingDate) : !holdingDateLookupDone);
    const showLoader = loading || awaitingAutoLoad;

    const clientOptions: SelectOption[] = useMemo(
        () => clients.map(client => {
            const typeSuffix = client.client_type === 'AIF'
                ? (client.firm_type === IFSC_AIF_FIRM_TYPE ? ' (IFSC AIF)' : ' (AIF)')
                : '';
            return {
                value: String(client.id),
                label: `${client.client_code} - ${getClientName(client)}${typeSuffix}`,
                id: client.id,
            };
        }),
        [clients],
    );

    const selectedOption = useMemo(
        () => clientOptions.find(option => option.id === selectedClient) ?? null,
        [clientOptions, selectedClient],
    );

    // The client the displayed report belongs to (shown in the results header).
    const loadedClient = useMemo(
        () => clients.find(client => client.id === loadedClientId) ?? null,
        [clients, loadedClientId],
    );

    const handleLoadPerformanceData = () => {
        if (!selectedClient || !holdingDate) {
            toast.warning('Please select a client and holding date');
            return;
        }
        if (isDateRestricted(holdingDate)) {
            toast.error(`Data after ${accessCutoffDate} requires complete access.`);
            return;
        }
        fetchPerformanceData(selectedClient, holdingDate);
    };

    // True while the access-restriction API is still in flight on a deep-link load.
    const checkingAccess = !!initialClientId && !restrictionLoaded;

    // "Today" plus the preset dates, shown in the Quick Select popover.
    // Computed once on mount (presets are relative to "now"; not worth recomputing per render).
    const quickDateOptions = useMemo(
        () => [{ label: 'Today', value: toISO(new Date()) }, ...getDatePresets()],
        [],
    );

    // The IFSC payload has its own shape (rendered by IFSCPerformanceReport), so the
    // standard row builders only run for PMS/AIF/IBKR reports. An IBKR report renders
    // through this same standard layout — only its `currency` field differs (USD vs
    // the INR default), handled by the currency-aware column builders below.
    const ifscReport = performanceData && isIfscReportData(performanceData) ? performanceData : null;
    const standardReport = performanceData && !isIfscReportData(performanceData) ? performanceData : null;

    const currency = standardReport?.currency;
    const sectorMax = maxShare(standardReport?.sector_allocation);
    const holdingsMax = maxShare(standardReport?.holdings);
    const isAif = loadedClient?.client_type === 'AIF';
    const sectorColumns = useMemo(
        () => (isAif ? buildSectorColumnsAif : buildSectorColumns)(currency, sectorMax),
        [isAif, currency, sectorMax],
    );
    const holdingsColumns = useMemo(
        () => (isAif ? buildHoldingsColumnsAif : buildHoldingsColumns)(currency, holdingsMax),
        [isAif, currency, holdingsMax],
    );

    const benchmarkLabel = standardReport?.report_type === IBKR_REPORT_TYPE
        ? SP500_BENCHMARK_LABEL
        : BSE500_BENCHMARK_LABEL;
    const twrrColumns = useMemo(() => buildTwrrColumns(benchmarkLabel), [benchmarkLabel]);
    const xirrColumns = useMemo(() => buildXirrColumns(benchmarkLabel), [benchmarkLabel]);

    // Portfolio Value leads as the headline figure; the rest pair up beneath it.
    const summaryItems: SummaryItem[] = standardReport ? [
        { label: 'Portfolio Value', value: formatCurrency(standardReport.total_portfolio_value, currency), hero: true },
        { label: 'Inception Date', value: formatDate(standardReport.inception_date) },
        // Currency only appears for IBKR reports — Nuvama PMS/AIF has no `currency`
        // field (defaults to INR throughout, so calling it out would be redundant).
        ...(currency ? [{ label: 'Currency', value: currency }] : []),
        { label: 'Net Capital In (+) / Out (-)', value: formatCurrency(standardReport.net_capital_in_out, currency) },
        { label: 'Total Contribution', value: formatCurrency(standardReport.total_contribution, currency) },
        { label: 'Total Withdrawal', value: formatCurrency(standardReport.total_withdrawal, currency) },
        {
            label: 'Unrealized Gain',
            value: (
                <SignedValue
                    value={Number(standardReport.unrealized_gain)}
                    format={() => formatCurrency(standardReport.unrealized_gain, currency)}
                    empty={formatCurrency(standardReport.unrealized_gain, currency)}
                />
            ),
        },
        { label: 'Fees and Expenses', value: formatCurrency(standardReport.total_fees_expenses, currency) },
        { label: 'Accrued Income', value: formatCurrency(standardReport.total_accrued_income, currency) },
    ] : [];

    const twrrRows: PeriodRow[] = standardReport
        ? Object.keys(standardReport.twrr).map(period => ({
            period,
            value: standardReport.twrr[period],
            benchmark: standardReport.benchmark?.[period] ?? null,
        }))
        : [];

    const xirrRows: PeriodRow[] = standardReport
        ? Object.keys(standardReport.xirr).map(period => ({
            period,
            value: standardReport.xirr[period],
            benchmark: standardReport.benchmark_irr?.[period] ?? null,
        }))
        : [];

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Client Performance"
                    subtitle="A single client's allocation, capital flows and TWRR / XIRR returns as at a holding date."
                />
            }
        >
                {/* The page's controls, on their own card above the report. The standing
                    data caveat rides in its footer — it applies to every report, so it
                    belongs with the controls rather than as a banner over the results. */}
                <Surface
                    padded
                    footer={
                        <span className={reportStyles.caveat}>
                            <Icon name="info" size={13} />
                            Historical performance is limited to data from 2020 onward; the {benchmarkLabel} benchmark
                            is not TRI-based. Data may include minor inconsistencies.
                        </span>
                    }
                >
                    <div className={styles['filter-row']}>
                        <Box sx={{ width: 460, maxWidth: '100%' }}>
                            <FilterAsyncSelect
                                label="Client"
                                placeholder="Search by code or name..."
                                value={selectedOption}
                                onChange={newValue => setSelectedClient(newValue ? Number(newValue.value) : null)}
                                loadOptions={loadClientOptions}
                            />
                        </Box>

                        <TextField
                            type="date"
                            label="Holding Date"
                            variant="outlined"
                            size="small"
                            value={holdingDate}
                            onChange={(e) => setHoldingDate(clampDate(e.target.value))}
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { max: accessCutoffDate ?? undefined },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Quick select">
                                                <IconButton
                                                    size="small"
                                                    edge="end"
                                                    aria-label="Quick date presets"
                                                    onClick={(e) => setQuickAnchor(e.currentTarget)}
                                                >
                                                    <ChevronDown size={16} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ width: 220, maxWidth: '100%', '& .MuiInputBase-root': { height: '40px' } }}
                        />
                        <Menu
                            anchorEl={quickAnchor}
                            open={Boolean(quickAnchor)}
                            onClose={() => setQuickAnchor(null)}
                        >
                            {quickDateOptions.map((preset) => {
                                const restricted = isDateRestricted(preset.value);
                                const item = (
                                    <MenuItem
                                        disabled={restricted}
                                        onClick={() => { setHoldingDate(clampDate(preset.value)); setQuickAnchor(null); }}
                                    >
                                        {preset.label}
                                    </MenuItem>
                                );
                                // Wrap disabled items in a span so the tooltip still fires
                                // (MUI suppresses pointer events on disabled MenuItems).
                                return restricted ? (
                                    <Tooltip key={preset.value} title="Beyond your allowed date range" placement="left">
                                        <span>{item}</span>
                                    </Tooltip>
                                ) : (
                                    <React.Fragment key={preset.value}>{item}</React.Fragment>
                                );
                            })}
                        </Menu>

                        <Button
                            variant="primary"
                            onClick={handleLoadPerformanceData}
                            disabled={loading || checkingAccess || !selectedClient || !holdingDate}
                        >
                            {(loading || checkingAccess) ? 'Loading…' : 'Load Performance'}
                        </Button>
                    </div>
                </Surface>

                {isAccessRestricted && (
                    <Note tone="warning">
                        Data for this date requires complete access. Contact your administrator.
                    </Note>
                )}

                {showLoader ? (
                    <Surface>
                        <Placeholder kind="loading" message="Loading performance data…" />
                    </Surface>
                ) : error ? (
                    <ErrorDisplay
                        message={error}
                        type="error"
                        onRetry={selectedClient && holdingDate ? () => fetchPerformanceData(selectedClient, holdingDate) : undefined}
                    />
                ) : performanceData ? (
                    <>
                        <ReportHeading
                            code={loadedClient?.client_code}
                            name={loadedClient ? (
                                getClientName(loadedClient)
                            ) : loadingClients ? (
                                // Deep-linked report can render before the client list resolves;
                                // show a skeleton for the name rather than a bare placeholder.
                                <Skeleton variant="text" width={220} sx={{ display: 'inline-block' }} />
                            ) : (
                                'Performance report'
                            )}
                            badge={loadedClient?.client_type === 'AIF' && (
                                <Pill tone="info" plain>
                                    {loadedClient.firm_type === IFSC_AIF_FIRM_TYPE ? 'IFSC AIF' : 'AIF'}
                                </Pill>
                            )}
                            asOf={formatDate(performanceData.holding_date)}
                        />

                        {standardReport?.nav_date_snapped && (
                            <Note tone="info">
                                This report reflects the most recent available valuation date ({formatDate(standardReport.holding_date)}) rather than the requested date.
                            </Note>
                        )}

                        {performanceData.warnings && performanceData.warnings.length > 0 && (
                            <Note tone="warning">
                                <strong>
                                    Data quality {performanceData.warnings.length === 1 ? 'notice' : 'notices'} ({performanceData.warnings.length})
                                </strong>
                                <ul>
                                    {performanceData.warnings.map((warning, index) => (
                                        <li key={index}>{warning.message}</li>
                                    ))}
                                </ul>
                            </Note>
                        )}

                        {ifscReport ? (
                            <IFSCPerformanceReport data={ifscReport} />
                        ) : standardReport && (
                        <>
                        <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <SectionCard title="Portfolio Allocation">
                                    <AllocationBreakdown
                                        slices={(standardReport.asset_allocation ?? []).map(row => ({
                                            label: row.asset_class,
                                            percentage: row.percentage,
                                            value: formatCurrency(row.market_value, currency),
                                        }))}
                                    />
                                </SectionCard>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <SectionCard title="Performance Summary">
                                    <SummaryGrid items={summaryItems} />
                                </SectionCard>
                            </Grid>
                        </Grid>

                        <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <SectionCard title="TWRR Performance" subtitle="Time-weighted return, independent of the timing of capital flows.">
                                    <StaticTable data={twrrRows} columns={twrrColumns} rowKey={(row) => row.period} />
                                </SectionCard>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <SectionCard title="XIRR" subtitle="Benchmark IRR simulates portfolio cashflows into the respective benchmark.">
                                    <StaticTable data={xirrRows} columns={xirrColumns} rowKey={(row) => row.period} />
                                </SectionCard>
                            </Grid>
                        </Grid>

                        {standardReport.sector_allocation && standardReport.holdings && (
                            <Grid container spacing={1.5}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <SectionCard title="Sector Allocation" meta={`${standardReport.sector_allocation.length} sectors`}>
                                        <StaticTable data={standardReport.sector_allocation} columns={sectorColumns} rowKey={(row) => row.sector_name} maxBodyHeight={LONG_LIST_MAX_HEIGHT} />
                                    </SectionCard>
                                </Grid>

                                <Grid size={{ xs: 12, md: 8 }}>
                                    <SectionCard title="Portfolio Holdings" meta={`${standardReport.holdings.length} positions`}>
                                        <StaticTable data={standardReport.holdings} columns={holdingsColumns} rowKey={(row) => `${row.symbol_code}-${row.asset_class}`} maxBodyHeight={LONG_LIST_MAX_HEIGHT} />
                                    </SectionCard>
                                </Grid>
                            </Grid>
                        )}
                        </>
                        )}
                    </>
                ) : (
                    <Surface>
                        <Placeholder
                            message="No performance report yet"
                            hint={<>Pick a client and holding date above, then click <strong>Load Performance</strong>.</>}
                        />
                    </Surface>
                )}
        </PageFrame>
    );
};

export default ClientPerformance;